import { useMutation, useQuery } from '@tanstack/react-query';
import {
  deleteTechnicianSkillSet,
  getAssignableTechnicianSkillSets,
  getTechnicianSkillSets,
  postTechnicianSkillSet,
  putTechnicianSkillSet,
} from 'queries';
import { parseErrors, Toast } from 'helpers';
import { JsonDataItem } from 'types';

export const useTechnicianSkillSets = (technicianId?: string) => {
  const {
    data: skillSets,
    isLoading: isSkillSetsLoading,
    refetch: refetchSkillSets,
  } = useQuery(['technician-skill-sets', { technicianId }], getTechnicianSkillSets, {
    enabled: !!technicianId,
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const {
    data: assignableSkillSets,
    isLoading: isAssignableSkillSetsLoading,
    refetch: refetchAssignableSkillSets,
  } = useQuery(['assignable-technician-skill-sets', { technicianId }], getAssignableTechnicianSkillSets, {
    enabled: !!technicianId,
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: createSkillSet, isLoading: isSkillSetCreating } = useMutation(postTechnicianSkillSet, {
    onSuccess: () => {
      Toast.show({ description: 'New Skill has been added', type: Toast.Type.Success });
      refetchSkillSets();
      refetchAssignableSkillSets();
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: updateSkillSet, isLoading: isSkillSetUpdating } = useMutation(putTechnicianSkillSet, {
    onSuccess: () => {
      Toast.show({ description: 'The Skill has been successfully updated', type: Toast.Type.Success });
      refetchSkillSets();
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: deleteSkillSet, isLoading: isSkillSetDeleting } = useMutation(deleteTechnicianSkillSet, {
    onSuccess: () => {
      Toast.show({ description: 'The Skill has been successfully deleted', type: Toast.Type.Success });
      refetchSkillSets();
      refetchAssignableSkillSets();
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  return {
    skillSets,
    isSkillSetsLoading,
    refetchSkillSets,
    assignableSkillSets,
    isAssignableSkillSetsLoading,
    createSkillSet,
    isSkillSetCreating,
    updateSkillSet,
    isSkillSetUpdating,
    deleteSkillSet,
    isSkillSetDeleting,
  };
};
